import React, { useState, useCallback } from "react";
import { Copy, Check, Terminal, ExternalLink } from "lucide-react";
import syntaxHighlight from "../../utils/jsonViewer";

interface OutputPanelProps {
  hasOutputs: boolean;
  outputs: any;
  nodeIcon: React.ReactNode;
  emptyOutputsText: string;
}

// ── Preview helper ──────────────────────────────────────────────────────────
function getPreview(value: any): string {
  if (value === null) return "null";
  if (typeof value === "string")
    return value.length > 50 ? value.slice(0, 50) + "..." : value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (Array.isArray(value)) return `[${value.length} items]`;
  if (typeof value === "object") return `{${Object.keys(value).length} keys}`;
  return String(value);
}

function getTypeBadge(value: any): { label: string; cls: string } {
  if (value === null || value === undefined)
    return { label: "null", cls: "text-gray-400 bg-gray-100" };
  if (Array.isArray(value))
    return { label: `[${value.length}]`, cls: "text-purple-600 bg-purple-50" };
  const t = typeof value;
  const map: Record<string, string> = {
    string: "text-emerald-600 bg-emerald-50",
    number: "text-amber-600 bg-amber-50",
    boolean: "text-violet-600 bg-violet-50",
    object: "text-blue-600 bg-blue-50",
  };
  return { label: t, cls: map[t] || "text-gray-500 bg-gray-100" };
}

// ── Copy button ─────────────────────────────────────────────────────────────
const CopyBtn: React.FC<{ text: string; className?: string }> = ({
  text,
  className = "",
}) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className={`text-gray-400 hover:text-gray-600 transition-all ${className}`}
      title="Copy"
    >
      {copied ? (
        <Check className="w-3 h-3 text-emerald-500" />
      ) : (
        <Copy className="w-3 h-3" />
      )}
    </button>
  );
};

// ── Key row ─────────────────────────────────────────────────────────────────
const KeyRow: React.FC<{ keyName: string; value: any }> = ({
  keyName,
  value,
}) => {
  const isUrl =
    keyName === "url" &&
    typeof value === "string" &&
    value.startsWith("http");
  const { label, cls } = getTypeBadge(value);
  const copyText =
    typeof value === "object"
      ? JSON.stringify(value, null, 2)
      : String(value ?? "");

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-white transition-colors group">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold text-gray-800">
            {keyName}
          </span>
          <span
            className={`text-[8px] font-bold uppercase px-1 py-px rounded ${cls}`}
          >
            {label}
          </span>
        </div>
        {isUrl ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-blue-600 hover:text-blue-800 hover:underline truncate block font-mono mt-0.5 flex items-center gap-1"
          >
            {value}
            <ExternalLink className="w-2.5 h-2.5 shrink-0 inline" />
          </a>
        ) : (
          <span className="text-[10px] text-gray-500 truncate block font-mono mt-0.5">
            {getPreview(value)}
          </span>
        )}
      </div>
      <CopyBtn
        text={copyText}
        className="opacity-0 group-hover:opacity-100 shrink-0"
      />
    </div>
  );
};

// ── Main panel ──────────────────────────────────────────────────────────────
export const OutputPanel: React.FC<OutputPanelProps> = ({
  hasOutputs,
  outputs,
  nodeIcon,
  emptyOutputsText,
}) => {
  const [copied, setCopied] = useState(false);
  const totalKeys = hasOutputs ? Object.keys(outputs).length : 0;

  const handleCopyAll = useCallback(() => {
    if (!outputs) return;
    navigator.clipboard.writeText(JSON.stringify(outputs, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [outputs]);

  return (
    <>
      {/* Header */}
      <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
            OUTPUT
          </h3>
          {hasOutputs && (
            <span className="text-[9px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full border border-emerald-200">
              {totalKeys} {totalKeys === 1 ? "key" : "keys"}
            </span>
          )}
        </div>
        {hasOutputs && (
          <button
            onClick={handleCopyAll}
            className="flex items-center gap-1 text-[10px] font-medium text-gray-500 hover:text-gray-700 transition-colors"
            title="Copy all"
          >
            {copied ? (
              <Check className="w-3 h-3 text-emerald-500" />
            ) : (
              <Copy className="w-3 h-3" />
            )}
            {copied ? "Copied" : "Copy"}
          </button>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-300">
        {hasOutputs ? (
          <div className="p-3 space-y-3">
            {/* JSON block */}
            <div className="rounded-lg overflow-hidden border border-gray-200 bg-white">
              <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-1.5 text-gray-400">
                  <Terminal className="w-3 h-3" />
                  <span className="text-[9px] font-mono">output.json</span>
                </div>
              </div>
              <pre
                className="p-3 bg-white text-[11px] font-mono leading-relaxed whitespace-pre-wrap break-all overflow-x-hidden"
                dangerouslySetInnerHTML={{
                  __html: syntaxHighlight(outputs),
                }}
              />
            </div>

            {/* Key summary rows */}
            <div className="space-y-0.5 bg-gray-50 rounded-lg border border-gray-100 py-1">
              {Object.entries(outputs).map(
                ([key, value]: [string, any]) => (
                  <KeyRow key={key} keyName={key} value={value} />
                ),
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center px-6">
            <div className="w-14 h-14 mb-3 bg-gray-100 rounded-xl flex items-center justify-center text-gray-300 border border-dashed border-gray-300">
              {nodeIcon}
            </div>
            <h4 className="text-xs font-semibold text-gray-700 mb-1">
              {emptyOutputsText}
            </h4>
            <p className="text-[10px] text-gray-400 leading-relaxed">
              Run the workflow to see results
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default OutputPanel;
